import React, { useEffect, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader } from 'lucide-react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [bookingDate, setBookingDate] = useState(null);

  useEffect(() => {
    // Get booking details from localStorage
    const storedDate = localStorage.getItem('booking_date');
    if (storedDate) {
      setBookingDate(new Date(storedDate));
    }

    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    let attempts = 0;
    const maxAttempts = 10;

    const poll = async () => {
      try {
        const response = await axios.get(`${API}/payment/status/${sessionId}`);
        setPaymentDetails(response.data);

        if (response.data.payment_status === 'paid') {
          setPaymentStatus('success');
          return;
        } else if (response.data.status === 'expired') {
          setPaymentStatus('expired');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setPaymentStatus('timeout');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setPaymentStatus('error');
      }
    };

    poll();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <section className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            {paymentStatus === 'checking' && (
              <Card className="border-2 border-yellow-400">
                <CardContent className="pt-12 pb-12 text-center">
                  <Loader className="w-16 h-16 text-yellow-600 mx-auto mb-4 animate-spin" />
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment...</h2>
                  <p className="text-gray-600">Please wait while we confirm your payment</p>
                </CardContent>
              </Card>
            )}

            {paymentStatus === 'success' && (
              <Card className="border-2 border-green-500">
                <CardContent className="pt-12 pb-12 text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">Payment Successful!</h1>
                  <p className="text-xl text-gray-700 mb-8">
                    Your deposit has been received. Thank you for booking with Thrifty Move!
                  </p>

                  <div className="bg-gray-50 p-6 rounded-lg mb-8 text-left">
                    <h3 className="font-semibold text-gray-900 mb-4">Booking Details</h3>
                    {bookingDate && (
                      <p className="text-gray-700 mb-2">
                        <strong>Moving Date:</strong> {bookingDate.toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    )}
                    <p className="text-gray-700 mb-2">
                      <strong>Deposit Paid:</strong> ${paymentDetails?.amount_total ? (paymentDetails.amount_total / 100).toFixed(2) : '0.00'} AUD
                    </p>
                    <p className="text-gray-700">
                      <strong>Status:</strong> <span className="text-green-600">Confirmed</span>
                    </p>
                  </div>

                  <div className="bg-yellow-50 border-2 border-yellow-400 p-6 rounded-lg mb-8">
                    <h3 className="font-semibold text-gray-900 mb-3">What's Next?</h3>
                    <ul className="text-left text-gray-700 space-y-2">
                      <li>✓ Check your email for payment confirmation</li>
                      <li>✓ Our team will contact you within 24 hours to confirm details</li>
                      <li>✓ Prepare for your move using our checklist</li>
                      <li>✓ Pay remaining balance on moving day</li>
                    </ul>
                  </div>

                  <div className="flex gap-4 justify-center">
                    <Button onClick={() => navigate('/')} className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
                      Back to Home
                    </Button>
                    <a href="tel:1800233023">
                      <Button variant="outline" className="border-2 border-gray-900">
                        Call Us: 1800 233 023
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            )}

            {(paymentStatus === 'error' || paymentStatus === 'timeout' || paymentStatus === 'expired') && (
              <Card className="border-2 border-red-500">
                <CardContent className="pt-12 pb-12 text-center">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Not Confirmed</h2>
                  <p className="text-gray-600 mb-8">
                    We couldn't confirm your payment. Please contact us to verify your booking.
                  </p>
                  <div className="flex gap-4 justify-center">
                    <a href="tel:1800233023">
                      <Button className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
                        Call 1800 233 023
                      </Button>
                    </a>
                    <Button onClick={() => navigate('/pay-deposit')} variant="outline">
                      Try Again
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
